// Attendance — Today board.
//
// Live view of who has come in / gone out today: KPI tiles (click to filter),
// today's employee table (click a row's IN/OUT cell to expand its full-day
// timeline). Auto-refreshes every minute;
// "Sync Now" pulls fresh punches from the SmartOffice biometric server.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Button, TextField } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import DashboardLayout from "../../layouts/DashboardLayout";
import FlatCard from "../../components/FlatCard";
import { useToast } from "../../components/ToastProvider";
import { useAttendanceToday, useSyncAttendance } from "../../hooks/queries";
import { exportToExcel } from "../../utils/excelExport";
import { COLORS } from "../../theme/tokens";
import {
  PageHeader,
  AttendanceError,
  KpiCard,
  DataTable,
  EmployeeCell,
  StatusChip,
  FilterChips,
  ExportButton,
  PunchSummary,
  firstPunchOf,
  lastPunchOf,
  middlePunches,
  useEmployeeEditor,
  InOutCount,
  fmtTime,
  fmtMinutes,
  fmtDate,
  fmtClock,
  timeAgo,
  fieldSx,
  primaryBtnSx,
  dailyRowsForExcel,
  DAILY_EXCEL_COLUMNS,
  dailyCellFill,
} from "./attendanceUi";

// Which rows each KPI / chip filter keeps.
const FILTERS = {
  all: () => true,
  present: (r) => ["present", "half_day", "in_office", "outside"].includes(r.status),
  late: (r) => r.isLate,
  absent: (r) => ["absent", "not_in"].includes(r.status),
  in_office: (r) => r.status === "in_office",
  outside: (r) => r.status === "outside",
  missed: (r) => r.status === "missed_punch",
};

function AttendanceToday() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, error, refetch, isFetching } = useAttendanceToday();
  const sync = useSyncAttendance();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const editor = useEmployeeEditor();

  const rows = useMemo(() => data?.rows || [], [data]);
  const s = data?.summary || {};
  const rules = data?.rules;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(FILTERS[filter] || FILTERS.all)
      .filter((r) => !q || `${r.name} ${r.employeeCode} ${r.department}`.toLowerCase().includes(q))
      .sort((a, b) => (a.firstIn || "~").localeCompare(b.firstIn || "~"));
  }, [rows, filter, search]);

  const presentCount = (s.present || 0) + (s.halfDay || 0) + (s.inOffice || 0) + (s.outside || 0);
  const absentCount = (s.absent || 0) + (s.notIn || 0);
  const toggle = (key) => setFilter((f) => (f === key ? "all" : key));

  async function handleSync() {
    try {
      const res = await sync.mutateAsync();
      toast.success(`Synced with biometric — ${res.fetched ?? 0} punch${res.fetched === 1 ? "" : "es"} today`);
    } catch (err) {
      toast.error(err.message || "Sync failed");
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      await exportToExcel({
        fileName: `Attendance-Today-${data.date}.xlsx`,
        sheets: [
          {
            name: "Today",
            title: `Attendance — ${fmtDate(data.date)}`,
            subtitle: rules ? `Shift ${fmtClock(rules.shiftStart)} – ${fmtClock(rules.shiftEnd)} · Grace ${rules.graceMinutes} min` : "",
            columns: DAILY_EXCEL_COLUMNS,
            rows: dailyRowsForExcel(visible),
            cellFill: dailyCellFill,
          },
        ],
      });
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  const kpis = [
    { key: "all", label: "Total Employees", value: s.total ?? "—", accent: COLORS.primary, icon: <GroupsOutlinedIcon /> },
    { key: "present", label: "Came Today", value: presentCount, accent: COLORS.success, icon: <HowToRegOutlinedIcon /> },
    { key: "in_office", label: "Inside Now", value: s.inOffice ?? 0, accent: COLORS.primary, icon: <MeetingRoomOutlinedIcon />, sub: "Last punch was IN" },
    { key: "outside", label: "Outside Now", value: s.outside ?? 0, accent: "#C2410C", icon: <LogoutOutlinedIcon />, sub: "Last punch was OUT" },
    { key: "late", label: "Late", value: s.late ?? 0, accent: COLORS.warning, icon: <AccessTimeOutlinedIcon />, sub: rules ? `After ${fmtClock(rules.shiftStart)} + ${rules.graceMinutes} min` : null },
    { key: "absent", label: "Not In / Absent", value: absentCount, accent: COLORS.danger, icon: <PersonOffOutlinedIcon /> },
  ];

  const columns = [
    { key: "emp", label: "Employee", width: "minmax(200px, 1.6fr)", align: "left", render: (r) => <EmployeeCell name={r.name} code={r.employeeCode} department={r.department} onEdit={editor.canEdit ? () => editor.open(r) : undefined} /> },
    { key: "in", label: "First Punch", width: "minmax(95px, 1fr)", align: "center", render: (r) => <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: r.isLate ? COLORS.warning : COLORS.ink }}>{fmtTime(firstPunchOf(r))}</Typography> },
    { key: "last", label: "Last Punch", width: "minmax(95px, 1fr)", align: "center", render: (r) => <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: COLORS.ink }}>{fmtTime(lastPunchOf(r))}</Typography> },
    { key: "timeline", label: "IN / OUT (click)", width: "minmax(175px, 1fr)", align: "center", render: (r) => <PunchSummary punches={middlePunches(r.punches)} /> },
    { key: "count", label: "Count", width: "minmax(105px, 1fr)", align: "center", render: (r) => <InOutCount inCount={r.inCount} outCount={r.outCount} /> },
    { key: "inside", label: "Inside", width: "minmax(75px, 1fr)", align: "center", render: (r) => fmtMinutes(r.insideMinutes ?? r.workMinutes) },
    { key: "outside", label: "Outside", width: "minmax(75px, 1fr)", align: "center", render: (r) => fmtMinutes(r.outsideMinutes) },
    { key: "status", label: "Status", width: "minmax(130px, 1fr)", align: "center", render: (r) => <StatusChip status={r.status} late={r.isLate} early={r.isEarly} /> },
  ];

  return (
    <DashboardLayout>
      <PageHeader
        title="Attendance"
        subtitle={
          data
            ? `${fmtDate(data.date, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}${rules ? ` · Shift ${fmtClock(rules.shiftStart)} – ${fmtClock(rules.shiftEnd)}` : ""}`
            : error ? "Biometric server offline" : "Loading…"
        }
        actions={
          <>
            <Typography sx={{ fontSize: 12, color: COLORS.faint, mr: 0.5 }}>
              Last synced {timeAgo(data?.lastSyncAt)}
            </Typography>
            <ExportButton onClick={handleExport} busy={exporting || !data} />
            <Button onClick={handleSync} disabled={sync.isPending} startIcon={<SyncIcon sx={{ animation: sync.isPending ? "spin 1s linear infinite" : "none", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }} />} sx={primaryBtnSx}>
              {sync.isPending ? "Syncing…" : "Sync Now"}
            </Button>
          </>
        }
      />
      <AttendanceError error={error} onRetry={() => refetch()} retrying={isFetching} />

      {/* KPI tiles — click to filter the table */}
      {/* All 6 tiles on one line (smaller screens wrap 3-up / 2-up) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(6, minmax(0, 1fr))" }, gap: { xs: 1.5, md: 2 }, mb: 2.5 }}>
        {kpis.map((k) => (
          <KpiCard key={k.key} {...k} active={filter === k.key && k.key !== "all"} onClick={() => toggle(k.key)} />
        ))}
      </Box>

      {/* Today's table — full width */}
      <FlatCard>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.hairline}` }} alignItems={{ md: "center" }}>
            <TextField size="small" placeholder="Search name, code, department…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ ...fieldSx, flex: 1, minWidth: { md: 220 } }} />
            <FilterChips
              value={filter}
              onChange={setFilter}
              options={[
                { key: "all", label: "All", count: rows.length },
                { key: "present", label: "Present", count: presentCount },
                { key: "in_office", label: "Inside", count: s.inOffice ?? 0 },
                { key: "outside", label: "Outside", count: s.outside ?? 0 },
                { key: "late", label: "Late", count: s.late ?? 0 },
                { key: "absent", label: "Not In", count: absentCount },
                { key: "missed", label: "Missed Punch", count: s.missedPunch ?? 0 },
              ]}
            />
          </Stack>
          <DataTable
            columns={columns}
            rows={visible}
            loading={isLoading}
            emptyText="No employees match this filter"
            onRowClick={(r) => navigate(`/attendance/employee/${encodeURIComponent(r.employeeCode)}`)}
          />
        </FlatCard>
      {editor.dialog}
    </DashboardLayout>
  );
}

export default AttendanceToday;
